import { Box, Alert, CircularProgress, Divider } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";

import { useProductCategory } from "../../../../hooks/query";
import { CategoryDetails } from "./components";
import Breadcrumb from "./components/Breadcrumb";
import type { IProductCategory } from "../../../../interfaces";

export default function Edit() {
    const navigate = useNavigate();
    const { categoryId } = useParams<{ categoryId: string }>();
    const { useList, useUpdate, useDelete } = useProductCategory();
    const { data, isLoading, isError, error } = useList();
    const updateCategorySvc = useUpdate();
    const deleteCategorySvc = useDelete();

    const [category, setCategory] = useState<IProductCategory | null>(null);

    // Find the category from the list
    useEffect(() => {
        if (data && categoryId) {
            const categories = (data as IProductCategory[]) || [];
            const found = categories.find((c) => c.Id === categoryId);
            if (found) {
                setCategory(found);
            }
        }
    }, [data, categoryId]);

    async function handleUpdate(updatedData: Partial<IProductCategory>) {
        if (!categoryId || !category) return;

        try {
            const params = {
                Id: categoryId,
                Name: updatedData.Name,
                Description: updatedData.Description ?? undefined,
                DefaultUnitType: updatedData.DefaultUnitType ?? undefined,
                MeasurementTypes: updatedData.MeasurementTypes ?? undefined,
                IsActive:
                    updatedData.IsActive !== undefined
                        ? typeof updatedData.IsActive === "number"
                            ? updatedData.IsActive === 1
                            : updatedData.IsActive
                        : undefined,
            };
            await updateCategorySvc.mutateAsync(params);
            // Refresh the list to get updated data
            // The query will automatically refetch
        } catch (err) {
            console.error("Failed to update product category:", err);
        }
    }

    async function handleDelete() {
        if (!categoryId) return;

        try {
            await deleteCategorySvc.mutateAsync(categoryId);
            navigate("/admin/product-category/list");
        } catch (err) {
            console.error("Failed to delete product category:", err);
        }
    }

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <Alert color="danger">
                    {error instanceof Error ? error.message : "Failed to load product category"}
                </Alert>
            </Box>
        );
    }

    if (!category) {
        return (
            <Box flex={1} p={2}>
                <Alert color="warning">Product category not found</Alert>
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display={"flex"}
            flexDirection={"column"}
            maxHeight={"100%"}
            sx={{ overflow: "hidden", minWidth: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />
            <Box
                flex={1}
                sx={{
                    overflow: "auto",
                    minWidth: 0,
                }}
            >
                <CategoryDetails
                    category={category}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    isUpdating={updateCategorySvc.isPending}
                    isDeleting={deleteCategorySvc.isPending}
                />
            </Box>
        </Box>
    );
}
